import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CircleCheck, ArrowRight } from 'lucide-react';
import { PLANS } from '@agentbrush/shared';
import { ButtonColorful } from '../../components/ui/button-colorful/button-colorful.js';
import { AwardBadge } from '../../components/ui/award-badge/award-badge.js';
import {
  ScrollReveal,
  SCROLL_REVEAL_ITEM_CLASS,
} from '../../components/ui/scroll-reveal/scroll-reveal.js';
import { cn } from '../../lib/cn.js';

const TIERS = ['starter', 'pro', 'power'] as const;
type Tier = (typeof TIERS)[number];

const GENERATION_KEYS: Record<Tier, string> = {
  starter: 'pricing.generations.starter',
  pro: 'pricing.generations.pro',
  power: 'pricing.generations.power',
};

const FEATURE_KEYS: Record<Tier, readonly string[]> = {
  starter: [
    'pricing.features.starter.1',
    'pricing.features.starter.2',
    'pricing.features.starter.3',
    'pricing.features.starter.4',
  ],
  pro: [
    'pricing.features.pro.1',
    'pricing.features.pro.2',
    'pricing.features.pro.3',
    'pricing.features.pro.4',
  ],
  power: [
    'pricing.features.power.1',
    'pricing.features.power.2',
    'pricing.features.power.3',
    'pricing.features.power.4',
  ],
};

function formatPrice(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
}

export function PricingSection() {
  const { t } = useTranslation('landing');

  return (
    <section
      id="pricing"
      aria-labelledby="pricing-heading"
      className="mx-auto w-full max-w-[1280px] px-6 py-12"
    >
      <div className="flex flex-col items-center gap-3 text-center">
        <span className="font-mono text-caption uppercase tracking-[0.2em] text-muted">
          {t('pricing.eyebrow')}
        </span>
        <h2
          id="pricing-heading"
          className="text-[clamp(2rem,4vw,3rem)] font-semibold leading-[1.1] tracking-[-0.02em] text-primary"
        >
          {t('pricing.heading')}
        </h2>
        <p className="max-w-xl text-body text-secondary">{t('pricing.subheadline')}</p>
      </div>

      <ScrollReveal
        className="mt-6 grid grid-cols-1 items-stretch gap-5 min-[900px]:grid-cols-3"
        stagger={0.08}
        rise={24}
        duration={0.5}
        threshold="top 85%"
      >
        {TIERS.map((tier) => (
          <PricingCard key={tier} tier={tier} featured={tier === 'pro'} />
        ))}
      </ScrollReveal>
    </section>
  );
}

interface PricingCardProps {
  tier: Tier;
  featured: boolean;
}

function PricingCard({ tier, featured }: PricingCardProps) {
  const { t } = useTranslation('landing');
  const plan = PLANS[tier];
  const features = FEATURE_KEYS[tier];

  return (
    <div
      data-plan={tier}
      aria-label={`${plan.name} plan`}
      className={cn(
        'pricing-card relative flex flex-col rounded-lg border p-5 text-left',
        'transition-[border-color,background-color,box-shadow,scale] duration-300 ease-out hover:!scale-[1.02]',
        SCROLL_REVEAL_ITEM_CLASS,
        featured
          ? 'border-accent-lime/60 bg-surface shadow-sm ring-1 ring-accent-lime/20 hover:shadow-xl hover:shadow-accent-lime/20 hover:ring-accent-lime/60'
          : 'border-white/10 bg-surface/50 hover:border-white/25 hover:bg-surface/75 hover:shadow-lg hover:shadow-black/50',
      )}
    >
      {featured && (
        <div className="pointer-events-none absolute -right-4 -top-4 z-10 w-[240px] rotate-[8deg] drop-shadow-[0_10px_24px_rgba(0,0,0,0.4)]">
          <AwardBadge type="product-of-the-day" place={1} label="Most popular" />
        </div>
      )}
      <div className="text-center">
        <div className="inline-flex items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2.5 py-0.5 text-caption font-semibold',
              featured ? 'bg-white text-canvas' : 'bg-white/[0.04] text-secondary',
            )}
          >
            {plan.name}
          </span>
        </div>

        <h3 className="mb-1 mt-3 text-[1.75rem] font-semibold leading-none tracking-tight text-primary">
          ${formatPrice(plan.price)}
          <span className="ml-1 text-small font-normal text-secondary">/mo</span>
        </h3>
        <span className="sr-only">{t('pricing.perMonth', { price: formatPrice(plan.price) })}</span>
        <p className="text-small text-secondary">
          {t('pricing.tokens', { count: plan.tokens })} · {t(GENERATION_KEYS[tier])}
        </p>
      </div>

      <div className="my-4 border-t border-white/10" />

      <ul className="space-y-2">
        {features.map((key) => (
          <li key={key} className="flex items-center text-small text-secondary">
            <CircleCheck
              className={cn(
                'mr-2 h-4 w-4 shrink-0',
                featured ? 'text-accent-lime' : 'text-accent-cyan',
              )}
              aria-hidden="true"
            />
            <span>{t(key)}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-4">
        <p className="mb-2 text-caption text-muted">
          {plan.overage ? t('pricing.overage') : t('pricing.noOverage')}
        </p>
        {featured ? (
          <Link to="/sign-up" className="block no-underline">
            <ButtonColorful className="h-11 w-full" endIcon={<ArrowRight className="h-4 w-4" />}>
              {t('pricing.cta')}
            </ButtonColorful>
          </Link>
        ) : (
          <Link
            to="/sign-up"
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md border border-white/10 bg-white/[0.03] px-4 text-sm font-semibold text-primary transition-colors hover:border-white/20 hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
          >
            {t('pricing.cta')}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        )}
      </div>
    </div>
  );
}
